/**
 * UDP Progress Fields — Single source of truth for step progress counting.
 *
 * Used by both frontend (stepper UI) and backend (computeStepProgressCounts).
 * Each step lists the curated fields that determine filled/total progress.
 *
 * Dot-paths (e.g., "currentAddress.state") resolve nested subdocument fields.
 * Only these fields count toward progress — extra fields in the schema are ignored.
 *
 * Progress is VISIBILITY-AWARE: a field only counts toward `total` (the
 * denominator) when it is actually applicable to the user's own answers,
 * evaluated via `isFieldVisible`. A class_12 user is not asked for degree
 * records, and someone who lists no siblings is not asked for sibling links,
 * so those fields are excluded from both the numerator and the denominator.
 * This mirrors the conditional rendering the stepper already performs, so the
 * "%" reflects "how much of what applies to YOU is filled" rather than a fixed
 * field count. See `conditionalOn` in profile-fields/* + `isFieldVisible`.
 */

import { isFieldVisible } from './helpers.js';

export const UDP_PROGRESS_FIELDS: Record<number, readonly string[]> = {
  1: [
    // 'fullName' is a synthetic key — counts as filled if ANY of
    // firstName / middleName / lastName is set. See UDP_PROGRESS_SYNTHETIC.
    'fullName',
    // The user-chosen display name (its own step-1 sub-step) counts on its
    // own, distinct from the legal-name parts above.
    'displayName',
    'gender',
    'dateOfBirth',
    'whatsappNumber',
    'primaryLanguage',
    'secondaryLanguages',
    'familyReligion',
    'personalReligion',
    'reservationStatus',
    'minorityStatus',
  ],
  2: [
    'politicalSpectrum',
    'selfPartyAssociation',
    'partyAssociationRole',
    'familyPartyAssociation',
    'relativesInPolitics',
    // Level/year are inline within the parent yes/no screens — only the
    // 2 parent fields contribute to step progress (the user can complete
    // the screen by answering yes/no without filling level/year).
    'hasContestedElection',
    'hasHeldPublicOffice',
    'networkAndCivicEngagement',
  ],
  3: [
    'epicNumber',
    'willVoteThisTime',
    'didVoteLastTime',
    'votingFrequency',
    'primaryVotingIssues',
  ],
  4: [
    'participateInRevolution',
    'leadershipSkillset',
    'politicalExposure',
    'comfortWithVisibility',
    'socialInfluenceLevel',
    'pastExperience',
    'desiredPoliticalPosition',
  ],
  5: ['mbtiType', 'oceanScores'],
  6: [
    'highestQualification',
    // Class 10
    'education10th.board',
    'education10th.schoolName',
    'education10th.yearOfPassing',
    'education10th.subjects',
    'education10th.marksObtained',
    // Class 12
    'education12th.institutionName',
    'education12th.boardOrUniversity',
    'education12th.yearOfPassing',
    'education12th.courseOrStream',
    'education12th.subjects',
    'education12th.marksOrGrade',
    // Degree repeaters + certificates + notes
    'diplomas',
    'graduations',
    'postGraduations',
    'doctorates',
    'postDoctorates',
    'otherCertificates',
  ],
  7: [
    'employmentStatus',
    'occupation',
    'workShift',
    'comfortableWithShift',
    'desiredShift',
    'incomeRange',
    'dreamOccupation',
  ],
  8: [
    'neurodiverseIdentity',
    'neurodevelopmentalConditions',
    'mentalHealthConditions',
    'personalityPatterns',
    'neurologicalChronicConditions',
    'ageOfRealization',
    'sensoryProfile',
    'cognitiveStrengths',
    'executiveFunctionChallenges',
    'learningStyle',
    'communicationPreference',
    'supportAndAccommodations',
    'maskingAndBurnout',
    'impactOnLife',
  ],
  9: [
    'disabilityStatus',
    'generalHealth',
    'healthInsurance',
    'diet',
    'physicalActivity',
    'sleepQuality',
    'substanceUse',
    'stressLevel',
  ],
  10: [
    'viewOnAnimalCruelty',
    'viewOnLgbtqStatements',
    'viewOnTransgenderInclusion',
    'facingCasteDiscrimination',
    'casteDiscriminationPlaces',
    'viewOnGenderEquality',
  ],
  11: [
    'currentAddress.state',
    'currentAddress.district',
    'currentAddress.village',
    'permanentAddress.state',
    'permanentAddress.district',
    'permanentAddress.village',
    'homeNativePlace.state',
    'homeNativePlace.district',
    'ancestralPlace.state',
    'ancestralPlace.district',
  ],
  12: [
    'maritalStatus',
    'familyMembers',
    // Family-link sub-steps — counted toward step-12 progress like any
    // other field. Conditional rendering means missing ones don't block
    // completion.
    'linkedMother',
    'linkedStepMother',
    'linkedFather',
    'linkedStepFather',
    'linkedSpouses',
    'linkedBloodSisters',
    'linkedHalfSisters',
    'linkedStepSisters',
    'linkedBloodBrothers',
    'linkedHalfBrothers',
    'linkedStepBrothers',
    'linkedDaughters',
    'linkedSons',
    'linkedOthers',
    'numberOfDependents',
    'housingType',
    'householdIncome',
    'bplCard',
    'digitalAccess',
    'migrationStatus',
    'landOwnership',
    'primaryIncomeSource',
  ],
} as const;

/** Total number of UDP steps */
export const UDP_TOTAL_STEPS = 12;

/**
 * Synthetic progress keys — keys that aren't real fields but represent
 * a group of underlying fields. Counted as "filled" if ANY underlying
 * field is filled. Used for UI sub-steps that collect multiple fields
 * at once (e.g. the fullName sub-step that saves firstName, middleName,
 * and lastName).
 */
export const UDP_PROGRESS_SYNTHETIC: Record<string, readonly string[]> = {
  fullName: ['firstName', 'middleName', 'lastName'],
} as const;

/**
 * Resolve a dot-path (e.g., "currentAddress.state") against a data object.
 * Works with both plain objects and Mongoose subdocuments.
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/** Determine if a field value counts as "filled" for progress */
export function isFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    // Area entity ref - check for name
    if ('name' in value && typeof (value as Record<string, unknown>).name === 'string') {
      return (value as Record<string, unknown>).name !== '';
    }
    return Object.keys(value).length > 0;
  }
  return false;
}

/**
 * Calculate progress for a single step.
 * Shared between frontend (stepper UI) and backend (API response).
 */
export function calculateStepProgress(
  stepNumber: number,
  formData: Record<string, unknown> | undefined | null,
): { filled: number; total: number } {
  const fields = UDP_PROGRESS_FIELDS[stepNumber];
  if (!fields) return { filled: 0, total: 0 };

  // `isFieldVisible` reads the controlling field off the same object, so an
  // untouched step (null/undefined) is treated as an empty form: its
  // always-visible fields still count as "to do", while gated fields whose
  // trigger is unanswered are (correctly) excluded until the gate is set.
  const data = formData ?? {};

  let filled = 0;
  let total = 0;
  for (const fieldPath of fields) {
    // Visibility is a property of the top-level field. Dot-paths (e.g.
    // "education10th.board") inherit their parent's `conditionalOn`, so we
    // evaluate visibility on the ROOT segment — `getFieldMeta` is keyed by
    // top-level field key and returns undefined (→ always visible) for a
    // dotted path.
    const dot = fieldPath.indexOf('.');
    const rootKey = dot === -1 ? fieldPath : fieldPath.slice(0, dot);
    if (!isFieldVisible(rootKey, data)) continue;

    total++;

    // Synthetic keys (e.g., "fullName") — filled if ANY underlying field is filled.
    const syntheticPaths = UDP_PROGRESS_SYNTHETIC[fieldPath];
    if (syntheticPaths) {
      if (syntheticPaths.some((p) => isFieldFilled(getNestedValue(data, p)))) {
        filled++;
      }
      continue;
    }
    if (isFieldFilled(getNestedValue(data, fieldPath))) {
      filled++;
    }
  }

  return { filled, total };
}

/**
 * Calculate overall progress across all steps.
 * Returns per-step breakdown + overall percentage.
 *
 * Mirrors dating-schema's calculateDatingOverallProgress().
 *
 * @param stepData - Map of step number → form data (e.g., { 1: {...}, 2: {...} })
 */
export function calculateOverallProgress(
  stepData: Record<number, Record<string, unknown> | undefined | null>,
): {
  steps: { stepNumber: number; filled: number; total: number }[];
  totalFilled: number;
  totalFields: number;
  percent: number;
} {
  const steps: { stepNumber: number; filled: number; total: number }[] = [];
  let totalFilled = 0;
  let totalFields = 0;

  for (let stepNumber = 1; stepNumber <= UDP_TOTAL_STEPS; stepNumber++) {
    const { filled, total } = calculateStepProgress(stepNumber, stepData[stepNumber]);
    steps.push({ stepNumber, filled, total });
    totalFilled += filled;
    totalFields += total;
  }

  const percent = totalFields > 0 ? Math.round((totalFilled / totalFields) * 100) : 0;

  return { steps, totalFilled, totalFields, percent };
}
